import React from 'react';
import { Link } from 'react-router';
import type { DetectionHistoryItem, PackageDetectionPayload } from '@redbax/shared';
import { formatDateTime } from '@redbax/shared';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Badge,
} from '@redbax/ui-kit';

function actionBadgeVariant(action: string) {
  switch (action) {
    case 'Block':
    case 'VulnBlock':
      return 'destructive' as const;
    case 'Warn':
    case 'AgeBlock':
      return 'warning' as const;
    case 'Log':
      return 'secondary' as const;
    default:
      return 'outline' as const;
  }
}

function tryParsePayload(raw: string): PackageDetectionPayload | null {
  try {
    return JSON.parse(raw) as PackageDetectionPayload;
  } catch {
    return null;
  }
}

interface PackageDetectionModalProps {
  event: DetectionHistoryItem;
  open: boolean;
  onClose: () => void;
}

export function PackageDetectionModal({ event, open, onClose }: PackageDetectionModalProps) {
  const payload = tryParsePayload(event.payload);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Package Security Detection</DialogTitle>
          <DialogDescription>
            Event detected at {formatDateTime(event.occurredAt)}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {payload && (
            <div>
              <p className="text-xs font-medium text-text-secondary mb-1">Package</p>
              <Link
                to={`/package-security/packages/${encodeURIComponent(payload.ecosystem)}/${encodeURIComponent(payload.package_name)}`}
                className="text-sm text-cyan-info hover:underline font-mono inline-block"
              >
                {payload.ecosystem && <span className="text-text-secondary">{payload.ecosystem}/</span>}
                {payload.package_name}
                {payload.package_version && (
                  <span className="text-text-secondary">@{payload.package_version}</span>
                )}
              </Link>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-medium text-text-secondary mb-1">Category</p>
              <Badge variant="outline">{event.category}</Badge>
            </div>
            <div>
              <p className="text-xs font-medium text-text-secondary mb-1">Action Taken</p>
              <Badge variant={actionBadgeVariant(event.actionTaken)}>{event.actionTaken}</Badge>
            </div>
          </div>

          {payload?.reason && (
            <div>
              <p className="text-xs font-medium text-text-secondary mb-1">Reason</p>
              <p className="text-sm text-text-primary">{payload.reason}</p>
            </div>
          )}

          <div>
            <p className="text-xs font-medium text-text-secondary mb-1">Target Application</p>
            <p className="text-sm text-text-primary">{event.targetApplication}</p>
          </div>

          <div>
            <p className="text-xs font-medium text-text-secondary mb-1">Hostname</p>
            <p className="text-sm text-text-primary">{event.hostname}</p>
          </div>

          {payload?.vulnerability_ids && payload.vulnerability_ids.length > 0 && (
            <div>
              <p className="text-xs font-medium text-text-secondary mb-1">Vulnerability IDs</p>
              <div className="flex flex-wrap gap-2">
                {payload.vulnerability_ids.map((id) => (
                  <a
                    key={id}
                    href={`https://osv.dev/vulnerability/${id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-cyan-info hover:underline font-mono"
                  >
                    {id}
                  </a>
                ))}
              </div>
            </div>
          )}

          {payload?.package_name && payload?.ecosystem && (
            <div>
              <Link
                to={`/package-security/packages/${encodeURIComponent(payload.ecosystem)}/${encodeURIComponent(payload.package_name)}`}
                className="text-sm text-cyan-info hover:underline"
              >
                View all vulnerabilities for this package
              </Link>
            </div>
          )}

          <div>
            <p className="text-xs font-medium text-text-secondary mb-1">Event ID</p>
            <p className="text-sm text-text-secondary font-mono truncate" title={event.eventId}>
              {event.eventId}
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
